// Autoplay unlock: browsers block Audio.play() until the page receives a user
// gesture. On the first pointer/key/touch we prime EVERY cached Audio element
// used by notifySound by playing it muted and immediately pausing. After the
// first successful prime we drop the gesture listeners.
import { getAllCachedSounds } from "@/lib/notifySound";
import { primeAtlasResponseAudio } from "@/lib/lrDirect/playAtlasResponse";

let installed = false;
let unlocked = false;

async function primeAll(): Promise<boolean> {
  // The ATLAS voice-response element plays long after the tap (post → navigate),
  // so it must be primed on the same gesture as the notification sounds.
  const atlasOk = await primeAtlasResponseAudio();
  const audios = getAllCachedSounds();
  if (audios.length === 0) return atlasOk;
  let anyOk = atlasOk;
  await Promise.all(
    audios.map(async (a) => {
      try {
        const prevMuted = a.muted;
        const prevVol = a.volume;
        a.muted = true;
        a.volume = 0;
        const p = a.play();
        if (p && typeof p.then === "function") {
          await p;
        }
        try { a.pause(); a.currentTime = 0; } catch { /* noop */ }
        a.muted = prevMuted;
        a.volume = prevVol;
        anyOk = true;
        console.debug("[audio-unlock] primed", a.src);
      } catch (err) {
        // Missing file / still blocked — keep listeners so a later gesture retries.
        console.warn("[audio-unlock] prime failed", a.src, (err as Error)?.name ?? err);
      }
    }),
  );
  return anyOk;
}

export function installAudioUnlock() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const handler = async () => {
    if (unlocked) return;
    const ok = await primeAll();
    if (ok) {
      unlocked = true;
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("touchstart", handler);
      console.debug("[audio-unlock] unlocked");
    }
    // If nothing primed successfully, KEEP listeners for the next gesture.
  };
  window.addEventListener("pointerdown", handler, { passive: true });
  window.addEventListener("keydown", handler);
  window.addEventListener("touchstart", handler, { passive: true });
}
